import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from "../icons/Icon";
import { format } from 'date-fns';
import { findConflicts, buildConflictMap } from '../../utils/payrollConflicts';
import { parsePeriodToDate } from '../../utils/periodDate';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';
import { splitDayPillText } from './payrollGuards';

const EmployeePayrollList = ({
  employees,
  periodStart,
  periodEnd,
  onEmployeeUpdate,
  onSelectionChange,
  companySettings,
  payrollIndexRuns = [],
  currentRunId = null,
  initialSelectedIds = null,
  employeesLoading = false,
  employeesError = null, // C3: server compute failed — render a retryable error, not an empty list
  onRetry = null,
  busy = false // UX-1: advancing to Step 3 — lock controls so the selection can't change mid-fetch
}) => {
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Sort state
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Selection state
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());

  // Excluded section state
  const [showExcluded, setShowExcluded] = useState(false);

  // Eligible and excluded employees
  const [eligibleEmployees, setEligibleEmployees] = useState([]);
  const [excludedEmployees, setExcludedEmployees] = useState([]);

  // Per-employee DTR count = records present on working days (completedDays),
  // read straight off the server summary row — no separate DTR re-read.
  const getDtrCount = (employee) => employee.completedDays || 0;

  // Scheduled working days for THIS employee inside the period, clamped to their
  // own employment window. buildEmployeeDtr classifies every scheduled working day
  // as either recorded (completedDays) or missing (missingDays), so their sum is
  // exactly "days this employee was due to work".
  //
  // A REST DAY is in neither bucket by design: a worked rest day is real, paid
  // work (it lands in timeRecords and daysWorked) but it is not a day the
  // employee was scheduled for, so it must not make the row look short. Using
  // the period-level `workDays` prop here was the bug — it is one company-wide
  // number applied to every employee, so anyone whose own schedule differed
  // (and anyone who worked only rest days in the period) was reported as
  // "missing" records that were never due.
  const getScheduledDays = (employee) =>
    (employee.completedDays || 0) + (employee.missingDays || 0);

  // Per-employee unresolved-OT counts, read straight off the server summary row
  // (C7). Unconditional return: a row without otPending/otRejected yields zeroes
  // rather than undefined — getEmployeeWarnings dereferences this for every row.
  const otCountsFor = useCallback((employee) => ({
    pending: employee.otPending || 0,
    rejected: employee.otRejected || 0,
  }), []);

  // Employee eligibility — the server verdict is AUTHORITATIVE for hire/end-date,
  // salaryType and PII (C7). The client only overlays the payroll-conflict
  // exclusion, handled in the split loop below.
  //
  // Unconditional return: C9 guarantees no run is in flight at cutover, so every
  // row carries `isEligible`. A row that somehow lacks one is treated as ELIGIBLE
  // rather than throwing — the caller destructures this result.
  const checkEmployeeEligibility = useCallback((employee) => {
    if (employee.isEligible === undefined) return { eligible: true };
    return employee.isEligible
      ? { eligible: true }
      : { eligible: false, reason: employee.exclusionReason || 'Not eligible' };
  }, []);

  // Get partial period info. computePayrollEligibility always sets `partialPeriod`
  // and projects it into the summary, so the verdict always arrives with the
  // employee (the old date-parsing fallback here was unreachable, D6).
  const getPartialPeriodInfo = useCallback((employee) => {
    return employee.partialPeriod
      ? { partial: true, label: 'Partial period' }
      : { partial: false };
  }, []);

  // Get employee warnings
  const getEmployeeWarnings = useCallback((employee) => {
    const warnings = [];
    // Count the days the day-build actually found missing, per employee — not
    // the company-wide period total. See getScheduledDays above.
    const missingDays = employee.missingDays || 0;

    // M1: the DTR is OPTIONAL for monthly-paid employees (no record = paid) —
    // the missing-records nag applies to daily earn-up only. Monthly gaps are
    // handled by the M5 reconciliation strip in Step 3.
    if (employee.employment?.salaryType !== 'monthly' && missingDays > 0) {
      warnings.push({
        type: 'dtr',
        message: `Missing ${missingDays} time record${missingDays === 1 ? '' : 's'}`
      });
    }

    // Pay rate — the server summary always emits a derived `dailyRate`
    // (computePayrollEligibility.js:111), for monthly employees too, so the
    // legacy employment-field fallback is gone (C7).
    const isMonthlyType = employee.employment?.salaryType === 'monthly';
    if (!employee.dailyRate || employee.dailyRate <= 0) {
      warnings.push({
        type: 'salary',
        message: isMonthlyType ? 'No monthly salary configured' : 'No daily salary configured'
      });
    }

    // Bank-account warning. The server summary always carries a hasBank boolean
    // (computePayrollEligibility.js:117); the banking PII itself is omitted (C7).
    if (employee.hasBank === false) {
      warnings.push({
        type: 'bank',
        message: 'Missing bank account'
      });
    }

    // OT approval warnings. These are the only warnings on this step that HOLD Next
    // (ProcessPayroll's hasUnresolvedOT gate), so they carry `blocking` and render in
    // the danger hue — amber here means "look, but you can proceed", red means "this
    // is what's stopping you". Without the split, one pending OT sat in an identical
    // amber strip among 20 non-blocking "Missing N time records" rows.
    const ot = otCountsFor(employee);
    if (ot.pending > 0) {
      warnings.push({
        type: 'ot',
        blocking: true,
        message: `${ot.pending} OT record${ot.pending > 1 ? 's' : ''} pending approval`
      });
    }
    if (ot.rejected > 0) {
      warnings.push({
        type: 'ot',
        blocking: true,
        message: `${ot.rejected} OT record${ot.rejected > 1 ? 's' : ''} rejected`
      });
    }

    // PRD_ShiftRules C4/C5 (BL-031): more than one entry on a day holds Next (ProcessPayroll's
    // hasSplitDaySelected gate), so it is blocking — the row reads Blocked and names the dates.
    if (Array.isArray(employee.splitDayDates) && employee.splitDayDates.length > 0) {
      warnings.push({
        type: 'dtr-split',
        blocking: true,
        message: splitDayPillText(employee.splitDayDates)
      });
    }

    return warnings;
    // No longer depends on the period-level workDays — the missing count now
    // comes off the employee itself.
  }, [otCountsFor]);

  // Build conflict map from payroll index
  const conflictMap = useMemo(() => {
    if (!payrollIndexRuns || payrollIndexRuns.length === 0 || !periodStart || !periodEnd) return {};
    const allEmployeeIds = employees.map(emp => emp.employeeId || emp.id);
    const pStart = new Date(periodStart).toLocaleDateString('en-CA');
    const pEnd = new Date(periodEnd).toLocaleDateString('en-CA');
    const conflicts = findConflicts(payrollIndexRuns, pStart, pEnd, allEmployeeIds, currentRunId);
    return buildConflictMap(conflicts);
  }, [payrollIndexRuns, periodStart, periodEnd, employees, currentRunId]);

  // Process employees into eligible/excluded
  useEffect(() => {
    const eligible = [];
    const excluded = [];

    // When resuming a draft, employees were already vetted — skip re-checking for them
    const draftIds = initialSelectedIds ? new Set(initialSelectedIds) : null;

    employees.forEach(emp => {
      const empId = emp.employeeId || emp.id;

      // Draft employees bypass eligibility & conflict checks (pre-approved when saved)
      if (draftIds && draftIds.has(empId)) {
        eligible.push(emp);
        return;
      }

      // Check payroll conflict first
      const conflict = conflictMap[empId];
      if (conflict) {
        const statusLabel = conflict.status === 'completed' ? 'Completed'
          : conflict.status === 'pending-approval' ? 'Pending Approval'
          : 'Draft';
        // Guard corrupted/Timestamp index periods so the label never crashes format()
        const s = parsePeriodToDate(conflict.periodStart);
        const e = parsePeriodToDate(conflict.periodEnd);
        const startFormatted = s ? format(s, 'MMM d') : (conflict.periodStart || '?');
        const endFormatted = e ? format(e, 'MMM d') : (conflict.periodEnd || '?');
        excluded.push({
          ...emp,
          excludeReason: `Already in Payroll ${conflict.runId} (${startFormatted}–${endFormatted}, ${statusLabel})`,
          excludeType: 'conflict',
        });
        return;
      }

      // Check standard eligibility (hire date, termination, salary type)
      const { eligible: isEligible, reason } = checkEmployeeEligibility(emp);
      if (isEligible) {
        eligible.push(emp);
      } else {
        excluded.push({ ...emp, excludeReason: reason, excludeType: 'eligibility' });
      }
    });

    setEligibleEmployees(eligible);
    setExcludedEmployees(excluded);

    // UX-1: do NOT re-seed the selection while advancing to Step 3. The detail-merge
    // in handleNext enriches `employees` in place, which re-runs this effect; auto-
    // selecting all eligible here would clobber the user's deselection (e.g. silently
    // re-add a pending-OT employee they removed) — that employee then rides into Step 3
    // summary-only. The selection is frozen for the duration of the transition.
    if (busy) return;

    // Use draft's selection when resuming, otherwise auto-select all eligible
    const selection = draftIds
      ? new Set([...draftIds].filter(id => eligible.some(emp => (emp.employeeId || emp.id) === id)))
      : new Set(eligible.map(emp => emp.employeeId || emp.id));
    setSelectedEmployees(selection);
    if (onSelectionChange) {
      onSelectionChange(selection);
    }
  }, [employees, checkEmployeeEligibility, onSelectionChange, conflictMap, initialSelectedIds, busy]);

  // Get unique departments
  const departments = [...new Set(eligibleEmployees.map(emp =>
    emp.employment?.department || emp.department
  ))].filter(Boolean);

  // Filter and sort employees
  const filteredEmployees = eligibleEmployees.filter(emp => {
    // Select All selects exactly these rows, so this search also decides who a
    // search-then-Select-All puts in the run (PRD_EmployeeSearchStandardization C7).
    const matchSearch = matchesEmployeeSearch(emp, searchTerm);

    const empDept = emp.employment?.department || emp.department || '';
    const matchDept = !filterDepartment || empDept === filterDepartment;

    const warnings = getEmployeeWarnings(emp);
    const hasWarnings = warnings.length > 0;

    let matchStatus = true;
    // BL-064: Incomplete (one-sided punch) outranks Ready — a blocking row must
    // not match the Ready filter, and gets its own filter so blockers can be
    // isolated in one click.
    if (filterStatus === 'ready') matchStatus = !hasWarnings && emp.hasIncompleteRecords !== true;
    if (filterStatus === 'warning') matchStatus = hasWarnings;
    if (filterStatus === 'partial') matchStatus = getPartialPeriodInfo(emp).partial;
    if (filterStatus === 'incomplete') matchStatus = emp.hasIncompleteRecords === true;

    return matchSearch && matchDept && matchStatus;
  });

  // Sort employees
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    let aVal, bVal;

    switch (sortField) {
      case 'name':
        // Sort by lastName first, then firstName as secondary sort
        aVal = (a.personalInfo?.lastName || '').toLowerCase();
        bVal = (b.personalInfo?.lastName || '').toLowerCase();
        if (aVal === bVal) {
          aVal = (a.personalInfo?.firstName || '').toLowerCase();
          bVal = (b.personalInfo?.firstName || '').toLowerCase();
        }
        break;
      case 'id':
        aVal = a.employeeId || a.employeeCode || '';
        bVal = b.employeeId || b.employeeCode || '';
        break;
      case 'department':
        aVal = a.employment?.department || a.department || '';
        bVal = b.employment?.department || b.department || '';
        break;
      case 'rate':
        aVal = a.dailyRate ?? 0;
        bVal = b.dailyRate ?? 0;
        break;
      default:
        aVal = '';
        bVal = '';
    }

    // localeCompare for strings: a raw < / > compares character codes, so Ñ ranks
    // above every plain letter and Ñuñez sorts last. Numbers keep the raw compare.
    // Matches PayrollSummary / DeductionsStep / GrossIncomeBreakdown, which already do this.
    const c = typeof aVal === 'string' && typeof bVal === 'string'
      ? aVal.localeCompare(bVal)
      : aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    return sortDirection === 'asc' ? c : -c;
  });

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Selection handlers. All no-op while `busy` (advancing to Step 3) so the selected
  // set is frozen during the detail-fetch + auto-save — a hard guard backing the
  // disabled controls, so a stale render or odd event can't mutate the selection
  // mid-transition (which would otherwise persist a summary-only line, UX-1).
  const handleSelectAll = () => {
    if (busy) return;
    const newSelected = new Set(filteredEmployees.map(emp => emp.employeeId || emp.id));
    setSelectedEmployees(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  const handleDeselectAll = () => {
    if (busy) return;
    const newSelected = new Set();
    setSelectedEmployees(newSelected);
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  const handleToggleEmployee = (employeeId) => {
    if (busy) return;
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeId)) {
      newSelected.delete(employeeId);
    } else {
      newSelected.add(employeeId);
    }
    setSelectedEmployees(newSelected);
    // Notify parent of selection change
    if (onSelectionChange) {
      onSelectionChange(newSelected);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get initials for avatar
  const getInitials = (employee) => {
    const first = employee.personalInfo?.firstName?.charAt(0) || '';
    const last = employee.personalInfo?.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase();
  };

  // Get employee name - Format: "Last Name, First Name" for consistent sorting
  const getEmployeeName = (employee) => {
    const firstName = employee.personalInfo?.firstName || '';
    const lastName = employee.personalInfo?.lastName || '';
    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }
    return lastName || firstName || 'Unknown';
  };

  // Sort Icon Component - White color for dark header background
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  return (
    <div className="employee-selection">
      {/* Single card: header + toolbar + table + footer (matches Gross Income / Deductions / Summary) */}
      <div className="employee-selection-content">
        <div className="pw-header">
          <h2 className="pw-header__title">Eligible Employees</h2>
          {periodStart && periodEnd && (
            <span className="pw-header__badge">
              {format(parsePeriodToDate(periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(periodEnd), 'MMM d, yyyy')}
            </span>
          )}
        </div>

        <div className="pw-toolbar">
          <div className="pw-toolbar__controls">
            <input
              type="text"
              className="pw-toolbar__search"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={busy}
            />
            <select
              className="pw-toolbar__select"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              disabled={busy}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              className="pw-toolbar__select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              disabled={busy}
            >
              <option value="">All Status</option>
              <option value="ready">Ready</option>
              <option value="warning">Warning</option>
              <option value="partial">Partial Period</option>
              <option value="incomplete">Incomplete</option>
            </select>
          </div>
          <div className="es-select-actions">
            <button type="button" className="es-btn" onClick={handleSelectAll} disabled={busy}>Select All</button>
            <button type="button" className="es-btn" onClick={handleDeselectAll} disabled={busy}>Deselect All</button>
          </div>
        </div>

        {employeesLoading ? (
          <div className="es-state">
            <div className="es-spinner" />
            <p>Loading employees...</p>
          </div>
        ) : employeesError ? (
          /* C3: checked BEFORE the empty state — a failed server compute must never
             read as "no eligible employees", which is what it looked like before. */
          <div className="es-state">
            <div className="es-state__icon"><Icon name="alert-triangle" size={32} /></div>
            <h3>Couldn't load employees</h3>
            <p>{employeesError}</p>
            {onRetry && (
              <button type="button" className="btn-primary" onClick={onRetry}>
                Try Again
              </button>
            )}
          </div>
        ) : sortedEmployees.length === 0 ? (
          <div className="es-state">
            <div className="es-state__icon"><Icon name="users" size={32} /></div>
            <h3>No Eligible Employees</h3>
            <p>
              {eligibleEmployees.length === 0
                ? 'No employees with daily salary type found for this payroll period.'
                : 'No employees match your current filters.'}
            </p>
          </div>
        ) : (
          <>
            <div className="pw-table-scroll">
              <table className="pw-table pw-table--eligible">
                <thead>
                  <tr>
                    <th className="th-check">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.size === sortedEmployees.length && sortedEmployees.length > 0}
                        onChange={(e) => e.target.checked ? handleSelectAll() : handleDeselectAll()}
                        disabled={busy}
                      />
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('name')}>
                      Employee<SortIcon field="name" />
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('id')}>
                      ID<SortIcon field="id" />
                    </th>
                    <th className="sortable-header" onClick={() => handleSort('department')}>
                      Department<SortIcon field="department" />
                    </th>
                    <th className="text-right sortable-header" onClick={() => handleSort('rate')}>
                      Daily Rate<SortIcon field="rate" />
                    </th>
                    <th className="text-center">DTR</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEmployees.map(employee => {
                    const employeeId = employee.employeeId || employee.id;
                    const dtrCount = getDtrCount(employee);
                    const warnings = getEmployeeWarnings(employee);
                    const hasBlockingWarning = warnings.some(w => w.blocking);
                    const partialInfo = getPartialPeriodInfo(employee);
                    const isSelected = selectedEmployees.has(employeeId);

                    return (
                      <React.Fragment key={employeeId}>
                        <tr className={`employee-row ${isSelected ? 'selected' : ''}`}>
                          <td className="td-check">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleEmployee(employeeId)}
                              disabled={busy}
                            />
                          </td>
                          <td className="pw-name-cell">
                            <div className="pw-name-cell__inner">
                              <div className="pw-avatar">{getInitials(employee)}</div>
                              <span className="pw-name-cell__text">{getEmployeeName(employee)}</span>
                            </div>
                          </td>
                          <td>
                            <span className="employee-id">{employee.employeeId || employee.employeeCode}</span>
                          </td>
                          <td className="pw-cell--muted">
                            {employee.employment?.department || employee.department || 'Not set'}
                          </td>
                          <td className="text-right">
                            <span className="amount">{formatCurrency(employee.dailyRate ?? 0)}</span>
                          </td>
                          <td className={`text-center es-dtr ${(employee.missingDays || 0) > 0 ? 'es-dtr--short' : ''}`}>
                            {dtrCount} / {getScheduledDays(employee)}
                          </td>
                          <td className="text-center">
                            {/* BL-064: a one-sided punch blocks Next for the whole run, so it
                                outranks every other status — the row must never read Ready. */}
                            {employee.hasIncompleteRecords === true ? (
                              <span className="es-status es-status--incomplete">Incomplete</span>
                            ) : hasBlockingWarning ? (
                              <span className="es-status es-status--blocked">Blocked</span>
                            ) : partialInfo.partial ? (
                              <span className="es-status es-status--partial">Partial</span>
                            ) : warnings.length > 0 ? (
                              <span className="es-status es-status--warning">Warning</span>
                            ) : (
                              <span className="es-status es-status--ready">Ready</span>
                            )}
                          </td>
                        </tr>
                        {warnings.length > 0 && (
                          <tr className={`es-warning-row ${hasBlockingWarning ? 'es-warning-row--blocking' : ''}`}>
                            <td></td>
                            <td colSpan="6">
                              {warnings.map((warning, idx) => (
                                <span key={idx} className={`es-warning-pill ${warning.blocking ? 'es-warning-pill--blocking' : ''}`}><Icon name="alert-triangle" size={13} aria-hidden="true" /> {warning.message}</span>
                              ))}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pw-footer">
              <div className="pw-footer__stats">
                <div className="pw-footer__stat">
                  <span className="pw-footer__stat-label">Total Employees</span>
                  <span className="pw-footer__stat-value">{eligibleEmployees.length}</span>
                </div>
                <div className="pw-footer__stat">
                  <span className="pw-footer__stat-label">Selected</span>
                  <span className="pw-footer__stat-value">{selectedEmployees.size}</span>
                </div>
              </div>
              <span className="pw-footer__hint">
                Showing {sortedEmployees.length} of {eligibleEmployees.length} eligible employees
              </span>
            </div>
          </>
        )}
      </div>

      {/* Excluded Section — hidden while the C3 error state is up: it derives from the
          same stale `employees` array the error branch just refused to render, so
          leaving it visible reads as partial success under a card saying nothing loaded. */}
      {!employeesError && excludedEmployees.length > 0 && (
        <div className="es-excluded">
          <button
            type="button"
            className="es-excluded__toggle"
            onClick={() => setShowExcluded(!showExcluded)}
          >
            <span>
              <Icon name="ban" size={14} aria-hidden="true" /> {excludedEmployees.length} employee{excludedEmployees.length !== 1 ? 's' : ''} excluded from this payroll run
            </span>
            <span>{showExcluded ? <Icon name="chevron-up" size={14} /> : <Icon name="chevron-down" size={14} />}</span>
          </button>
          {showExcluded && (
            <div className="es-excluded__body">
              <table className="es-excluded__table">
                <tbody>
                  {excludedEmployees.map(employee => (
                    <tr key={employee.employeeId || employee.id}>
                      <td>
                        <div className="es-excluded__emp">
                          <div className="es-excluded__avatar">{getInitials(employee)}</div>
                          <span className="es-excluded__name">{getEmployeeName(employee)}</span>
                        </div>
                      </td>
                      <td>
                        <code className="es-excluded__id">{employee.employeeId || employee.employeeCode}</code>
                      </td>
                      <td className="text-right">
                        <span className={`es-excluded__reason ${employee.excludeType === 'conflict' ? 'es-excluded__reason--conflict' : ''}`}>
                          {employee.excludeReason}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeePayrollList;
